import type { MailboxInfo } from 'owa-client-types';
import type { StrictAcquisition } from '../types';
import { getAccountScopeUserSettings } from 'owa-session-store';
import { logUsage } from 'owa-analytics';
import { toJS } from 'mobx';

/**
 * Retrieves the cached M365 acquisition data from the startup data for a given mailbox.
 *
 * This function accesses the account-scoped user settings for the provided mailbox,
 * extracts the `MosAcquisition` property, and returns its `Root` property as a `StrictAcquisition`.
 * If no cached acquisition data is found, it returns `undefined`.
 *
 * @param mailboxInfo - The mailbox information used to retrieve user settings.
 * @returns The cached `StrictAcquisition` object if available; otherwise, `undefined`.
 */
export function getBootM365AcquisitionFromStartupData(
    mailboxInfo: MailboxInfo
): StrictAcquisition | undefined {
    let cachedStrictAcquisition: StrictAcquisition | undefined = undefined;
    let noCacheReason: string | undefined = undefined;
    const cachedMosAcquisitionStartupData =
        getAccountScopeUserSettings(mailboxInfo)?.MosAcquisition;

    if (cachedMosAcquisitionStartupData) {
        const cachedMosAcquisitionStartupDataRoot = cachedMosAcquisitionStartupData.Root;

        if (cachedMosAcquisitionStartupDataRoot) {
            const cachedAcquisitionFromStartupData =
                cachedMosAcquisitionStartupDataRoot.acquisition;
            if (cachedAcquisitionFromStartupData) {
                // Startup data's titleId may be a JS object or a plain text string, so we need to extract the id if present.
                const rawTitleId = toJS(cachedAcquisitionFromStartupData.titleId);
                const titleIdId =
                    typeof rawTitleId === 'object' && rawTitleId !== null && 'id' in rawTitleId
                        ? (
                              rawTitleId as {
                                  id: string;
                              }
                          ).id
                        : (rawTitleId as string);

                // Convert titleDefinition to JS style as it is an object from response.
                const titleDefinitionJs = toJS(cachedAcquisitionFromStartupData.titleDefinition);

                if (titleDefinitionJs) {
                    cachedStrictAcquisition = {
                        __typename: 'M365Acquisition',
                        appId: cachedAcquisitionFromStartupData.appId,
                        titleId: titleIdId,
                        titleDefinition: titleDefinitionJs,
                        acquisitionState: cachedAcquisitionFromStartupData.acquisitionState,
                        canBeUnacquired: cachedAcquisitionFromStartupData.canBeUnacquired,
                        acquisitionContext: cachedAcquisitionFromStartupData.acquisitionContext,
                        manifestId: cachedAcquisitionFromStartupData.manifestId,
                    } as StrictAcquisition;
                } else {
                    noCacheReason = 'EmptyTitleDefinition';
                }
            } else {
                noCacheReason = 'EmptyAcquisition';
            }
        } else {
            noCacheReason = 'EmptyStartupDataRoot';
        }
    } else {
        noCacheReason = 'EmptyMosStartupData';
    }

    logUsage('MosAcqDataFromStartup', mailboxInfo, {
        hasCache: cachedStrictAcquisition ? 'Y' : 'N',
        noCacheReason,
    });

    return cachedStrictAcquisition;
}
